import { QueryOptions, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { FC, ReactElement, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';

import API from '@api/API';

import { useAppSettingsQuery } from '@queries/AppQueries';
import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useCityById } from '@hooks/useCurrentUserLocation';

import { EntertainerDashboardAction, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { RecommendEntertainersPopup } from '../CityEntertainerStandoutsScreen/CityEntertainerStandoutsScreen';
import { EntertainerOnboardingLinkButton } from '../EntertainerDetailsScreen/EntertainerDetailsScreen';

export interface FeaturedLook {
  _id: string;
  title: string;
  image: string | undefined;
  createdAt: string;
  entertainer: {
    _id: string;
    title: string;
    cityIds: string[];
    bookingHandle: string;
    isDragKingThing: boolean;
    isBipocArtist: boolean;
  };
}

export const useFeaturedLooksQuery = (
  {
    cityId,
    category,
    badgeId,
  }: {
    cityId?: string;
    category?: string;
    badgeId?: string;
  },
  options: {
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
    refetchInterval?: number;
    enabled?: boolean;
    staleTime?: number;
  } = {},
) => {
  const { data, isFetched } = useQuery({
    queryKey: ['featuredLooks', cityId ?? 'all', category ?? 'all', badgeId ?? 'all'],
    queryFn: () => {
      const queryParams = new URLSearchParams();

      if (cityId) {
        queryParams.append('cityId', cityId);
      }

      if (category) {
        queryParams.append('category', category);
      }

      if (badgeId) {
        queryParams.append('badgeId', badgeId);
      }

      const queryString = queryParams.toString();

      return API.get<{ featuredLooks: FeaturedLook[] }>(
        `/v1/entertainer-media/featured?${queryString}`,
      );
    },
    ...options,
  });

  return {
    featuredLooks: data?.featuredLooks,
    featuredLooksAreReady: isFetched,
  };
};

export const FeaturedLookPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  entertainerMedia: {
    _id: string;
    title: string;
    image: string;
  };
  entertainer: {
    _id: string;
    title: string;
  };
  header?: ReactElement;
}> = ({ entertainerMedia, entertainer, isVisible, onClose, header }) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose} title="Featured Look" height="full">
      <Popup.Content>
        {entertainerMedia && (
          <>
            {header}
            <div style={{ marginBottom: '-.5rem' }}>{entertainer.title}</div>
            <TitleToolbar className="mt-0" text={entertainerMedia.title} />
            <div style={{ position: 'relative' }}>
              {false && (
                <div
                  className="p-3"
                  style={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    background: 'rgba(0,0,0,.6)',
                  }}
                >
                  <div style={{ marginBottom: '-.5rem' }}>{entertainer.title}</div>
                  <TitleToolbar className="m-0" text={entertainer.title} />
                </div>
              )}
              <AsyncImage src={entertainerMedia.image} style={{ width: '100%' }} />
              <div style={{ position: 'absolute', bottom: 0, width: '100%' }}>
                <div className="p-3">
                  <SendEntertainerBadgeButton
                    entertainerId={entertainer._id}
                    entertainerMediaId={entertainerMedia._id}
                    background="rgba(0,0,0,.6)"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </Popup.Content>
    </Popup>
  );
};

const PREV_NEXT_ENABLED = false;

export const FeaturedLooksScreen = createScreenComponent(({ f7route }) => {
  const { cityId } = f7route.query;
  const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({
    cityId,
  });
  const { city } = useCityById(cityId);
  const { entertainer } = useCurrentEntertainerQuery();
  const { navigate } = useRouter();

  const [currentLookIndex, setCurrentLookIndex] = useState(-1);
  const currentFeaturedLook = useMemo<FeaturedLook | undefined>(() => {
    return featuredLooks?.[currentLookIndex];
  }, [currentLookIndex, featuredLooks]);

  const featuredKingsThings = useMemo(() => {
    return featuredLooks.filter((look) => look.entertainer.isDragKingThing);
  }, [featuredLooks]);

  const featuredBipocArtists = useMemo(() => {
    return featuredLooks.filter((look) => look.entertainer.isBipocArtist);
  }, [featuredLooks]);

  const oneWeekAgo = dayjs().subtract(1, 'week');
  const featuredThisWeek = useMemo(() => {
    return featuredLooks.filter((look) => oneWeekAgo.isBefore(look.createdAt));
  }, [featuredLooks]);

  const setCurrentLookById = (featuredLookId: string) => {
    const index = featuredLooks.findIndex((look) => look._id === featuredLookId);
    setCurrentLookIndex(index);
  };

  return (
    <Screen
      name="Featured Looks Screen"
      headerProps={{
        title: <Screen.GagglTitle />,
        // right: 'Share',
      }}
      hideToolbar
      disableLocationPermissionGate
    >
      <Screen.Content maxWidth={425} screenContentClassName="p-0">
        {!entertainer?.title && <EntertainerOnboardingLinkButton back="/noauth/featured-looks" />}
        <div className="px-3">
          <TitleToolbar text="Featured Looks" className="mb-0" />
          <p className="mt-0">{`These are all shared looks by drag artists ${cityId && city?.name ? `in ${city?.name}` : `on Gaggl`}. Show your support to your favorites!`}</p>
          <TitleToolbar text="Shared This Week" size="sm" />
          {featuredThisWeek.length ? (
            <HorizontalScroll
              items={[
                <div className="d-flex gap-1 pe-4">
                  {featuredThisWeek.map((look) => (
                    <div
                      key={`featured-this-week-${look._id}`}
                      onClick={() => {
                        setCurrentLookById(look._id);
                      }}
                    >
                      <img
                        src={look.image}
                        alt={look.title}
                        style={{ width: 'auto', height: 120, borderRadius: 4 }}
                      />
                    </div>
                  ))}
                </div>,
              ]}
            />
          ) : (
            <EmptyState
              title="No looks this week"
              text="No one has shared any looks this week. Be the first!"
              button={{
                text: 'Share a look',
                onClick: () => {
                  navigate(EntertainerDashboardAction.AddLook);
                },
              }}
            />
          )}

          <TitleToolbar text="Trending Kings & Things" size="sm" />
          {featuredKingsThings.length ? (
            <HorizontalScroll
              items={[
                <div className="d-flex gap-1 pe-4">
                  {featuredKingsThings.map((look) => (
                    <div
                      key={`featured-king-thing-${look._id}`}
                      onClick={() => {
                        setCurrentLookById(look._id);
                      }}
                    >
                      <img
                        src={look.image}
                        alt={look.title}
                        style={{ width: 'auto', height: 120, borderRadius: 4 }}
                      />
                    </div>
                  ))}
                </div>,
              ]}
            />
          ) : (
            <EmptyState
              title="No featured kings & things"
              text="No kings or things have shared any looks yet. Recommend them to share!"
              button={{
                text: 'Recommend',
                onClick: () => {
                  navigate(EntertainerDashboardAction.MakeRec, {
                    state: { cityId, initialQuestionSlug: 'king', hideQuestionOptions: true },
                  });
                },
              }}
            />
          )}

          <TitleToolbar text="Trending BIPOC Artists" size="sm" />
          {featuredBipocArtists.length ? (
            <HorizontalScroll
              items={[
                <div className="d-flex gap-1 pe-4">
                  {featuredBipocArtists.map((look) => (
                    <div
                      key={`featured-king-thing-${look._id}`}
                      onClick={() => {
                        setCurrentLookById(look._id);
                      }}
                    >
                      <img
                        src={look.image}
                        alt={look.title}
                        style={{ width: 'auto', height: 120, borderRadius: 4 }}
                      />
                    </div>
                  ))}
                </div>,
              ]}
            />
          ) : (
            <EmptyState
              title="No featured bipoc artists"
              text="No bipoc artists have shared any looks yet. Recommend them to share!"
              button={{
                text: 'Recommend',
                onClick: () => {
                  navigate(EntertainerDashboardAction.MakeRec, {
                    state: {
                      cityId,
                      initialQuestionSlug: 'bipoc',
                      hideQuestionOptions: true,
                    },
                  });
                },
              }}
            />
          )}

          <TitleToolbar
            text={cityId && city?.name ? `Trending in ${city?.name}` : `Trending on Gaggl`}
            size="sm"
          />
        </div>
        {featuredLooksAreReady ? (
          <div className="d-grid grid-cols-3 gap-1 px-1">
            {featuredLooks.map(({ _id, image }, i) => (
              <div
                key={_id}
                style={{ aspectRatio: 1 }}
                onClick={() => {
                  setCurrentLookIndex(i);
                }}
              >
                <AsyncImage
                  src={image}
                  style={{
                    display: 'block',
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <Skeleton height={400} />
        )}
        <FeaturedLookPopup
          header={
            PREV_NEXT_ENABLED ? (
              <div className="d-flex justify-content-between mb-3">
                <div>
                  {currentLookIndex > 0 && (
                    <Button
                      variant="smooth"
                      color="neutral"
                      roundness="rounded"
                      onClick={() => setCurrentLookIndex((prevValue) => prevValue - 1)}
                    >
                      Previous
                    </Button>
                  )}
                </div>
                <div>
                  {currentLookIndex + 1 < featuredLooks.length && (
                    <Button
                      variant="smooth"
                      color="neutral"
                      roundness="rounded"
                      onClick={() => setCurrentLookIndex((prevValue) => prevValue + 1)}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <></>
            )
          }
          entertainerMedia={currentFeaturedLook}
          entertainer={currentFeaturedLook?.entertainer}
          isVisible={!!currentFeaturedLook}
          onClose={() => setCurrentLookIndex(-1)}
        />
      </Screen.Content>
    </Screen>
  );
});
